import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';

/**
 * The page an affiliate's referral link actually points to. Reads ?ref=code
 * from the URL, starts a real Stripe Checkout session, and redirects there —
 * client_reference_id carries the code through so the webhook can attribute
 * the sale to the right affiliate (see checkout.controller.js).
 *
 * 2026-09-16: also the real, universal signup entry point for non-affiliate
 * traffic — added real marketing attribution here (Jenn: "we need to see
 * why more people aren't joining... who we are interesting to"). Standard
 * UTM query params (utm_source/medium/campaign/content/term) are captured
 * automatically for anyone arriving through a tagged link — nothing for the
 * visitor to fill in. document.referrer is a technical fallback for organic
 * traffic that was never tagged at all. "How did you hear about us?" is the
 * one real, optional field a visitor can answer themselves.
 */
const HOW_HEARD_OPTIONS = [
  'Instagram', 'Facebook', 'TikTok', 'Pinterest', 'YouTube',
  'Google search', 'A friend or family member', 'Other',
];

export default function Join() {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [howHeard, setHowHeard] = useState('');
  const [referredByName, setReferredByName] = useState('');
  // Jenn, 2026-10-07: an affiliate's "10 days for $1" offer is a code the
  // person types in here, separate from the link. Stripe's own code box only
  // knows Stripe promotion codes, so trial codes need this box.
  const [couponCode, setCouponCode] = useState(searchParams.get('code') || searchParams.get('coupon') || '');
  // A $1 offer is once per person (Jenn, 2026-10-07): with a code, ask the
  // email they'll join with, in the same box. The server checks it and locks
  // it onto the checkout.
  const [codeEmail, setCodeEmail] = useState('');

  // First-party tracking (2026-09-28): one page view per visit, so an
  // affiliate's /join?ref= link counts as "Clicked your link". The server
  // sets/uses the 365reset.ca visitor cookie. Fire-and-forget — tracking
  // must never get in the way of joining.
  useEffect(() => {
    api.post('/track', {
      type: 'page_view', site: 'join', path: '/join',
      ref: referralCode || undefined,
      utmSource:   searchParams.get('utm_source')   || undefined,
      utmMedium:   searchParams.get('utm_medium')   || undefined,
      utmCampaign: searchParams.get('utm_campaign') || undefined,
      utmContent:  searchParams.get('utm_content')  || undefined,
      utmTerm:     searchParams.get('utm_term')     || undefined,
      referrerUrl: document.referrer || undefined,
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoin = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/checkout/session', {
        referralCode,
        couponCode: couponCode.trim() || undefined,
        email: couponCode.trim() ? (codeEmail.trim() || undefined) : undefined,
        howHeard: howHeard || undefined,
        referredByName: referredByName.trim() || undefined,
        utmSource:   searchParams.get('utm_source')   || undefined,
        utmMedium:   searchParams.get('utm_medium')   || undefined,
        utmCampaign: searchParams.get('utm_campaign') || undefined,
        utmContent:  searchParams.get('utm_content')  || undefined,
        utmTerm:     searchParams.get('utm_term')     || undefined,
        referrerUrl: document.referrer || undefined,
      });
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start checkout — please try again shortly.');
      // Offer already used / had a membership: clear the code so "Join now" is the regular price.
      if (err.response?.data?.trialUsed) setCouponCode('');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-reset-cream flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-8 text-center">
        <h1 className="font-serif text-3xl font-bold text-reset-brown mb-3">What if every part of your life worked for you?</h1>
        <p className="text-sm text-gray-500 mb-6">
          Your money, work, body, mind, heart and spirit. Most of us only need a tweak in one area.
          Some of us need more. 365reset is built for both.
        </p>
        {/* Price and what's included (Jenn, 2026-10-12): this page used to show
            neither, and people sent here by affiliates were leaving from it. */}
        <div className="mb-6 rounded-xl bg-reset-cream px-5 py-4 text-left">
          <p className="text-reset-brown"><span className="text-2xl font-bold">CA$20.58</span> <span className="text-sm">a month</span></p>
          <p className="text-xs text-gray-500 mb-3">Everything included. No tiers. Cancel anytime.</p>
          <ul className="space-y-1.5 text-sm text-reset-brown">
            <li>✓ The Hub: 10+ live sessions every week, cameras optional, replays up for 7 days (older ones by request)</li>
            <li>✓ The Learning Lab: short, practical courses across all six areas</li>
            <li>✓ The Compass: a daily check-in that takes under a minute</li>
            <li>✓ Three mentors-at-large: Jenn, Trish and Patti</li>
            <li>✓ A community that accepts you as you are</li>
          </ul>
        </div>
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>
        )}
        {/* "Who referred you?" (Jenn, 2026-09-27). If the name matches one of our
            affiliates they're credited automatically; otherwise Team365 sees
            the name on the new member's profile. Hidden when they arrived
            through an affiliate's own link — that link already credits them. */}
        {!referralCode && (
          <div className="mb-4 text-left">
            <label htmlFor="referred-by" className="block text-xs font-semibold text-gray-500 mb-1.5">
              Who referred you? <span className="font-normal text-gray-400">(optional — their name)</span>
            </label>
            <input
              id="referred-by"
              type="text"
              maxLength={200}
              value={referredByName}
              onChange={(e) => setReferredByName(e.target.value)}
              placeholder="e.g. Sue Sutcliffe"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
          </div>
        )}
        <div className="mb-5 text-left">
          <label htmlFor="how-heard" className="block text-xs font-semibold text-gray-500 mb-1.5">
            How did you hear about us? <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <select
            id="how-heard"
            value={howHeard}
            onChange={(e) => setHowHeard(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          >
            <option value="">Select one…</option>
            {HOW_HEARD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div className="mb-5 text-left">
          <label htmlFor="offer-code" className="block text-xs font-semibold text-gray-500 mb-1.5">
            Have a code? <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            id="offer-code"
            type="text"
            maxLength={60}
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            autoCapitalize="characters"
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          {couponCode.trim() && (
            <>
              <label htmlFor="code-email" className="block text-xs font-semibold text-gray-500 mt-3 mb-1.5">
                The email you'll join with
              </label>
              <input
                id="code-email"
                type="email"
                autoComplete="email"
                maxLength={254}
                value={codeEmail}
                onChange={(e) => setCodeEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
              />
            </>
          )}
        </div>
        <button
          onClick={handleJoin}
          disabled={loading}
          className="w-full px-6 py-3 bg-reset-terracotta text-white text-sm font-semibold rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
        >
          {loading ? 'Starting checkout…' : 'Join now'}
        </button>
      </div>
    </div>
  );
}
